import React, { useEffect } from "react";

import AppController, { AppControllerState } from "js/core/AppController";
import WorkspaceController, { WorkspaceControllerState } from "js/controllers/WorkspaceController";
import PresentationLibraryPaneController, { PresentationLibraryFilterType } from "js/controllers/PresentationLibraryPaneController";

export interface PresentationLibraryPaneControllerInitializerProps {
    children: React.ReactElement<any, string | React.JSXElementConstructor<any>>;
    pane?: PresentationLibraryFilterType;
    folderId?: string;
    subFolderId?: string;
    presentationIds?: string[];
}

export const PresentationLibraryPaneControllerInitializer = AppController.withState(WorkspaceController.withState(PresentationLibraryPaneController.withState(
    function PresentationLibraryPaneControllerInitializer({
        pane,
        folderId,
        subFolderId,
        children,
        user,
        workspaceId,
        presentationIds
    }: PresentationLibraryPaneControllerInitializerProps & WorkspaceControllerState & AppControllerState) {
        const parentControllersInitialized = AppController.initialized && WorkspaceController.initialized;

        useEffect(() => {
            if (!parentControllersInitialized) {
                return;
            }

            pane = pane ?? PresentationLibraryFilterType.ALL_PRESENTATIONS;

            const librarySettings = user.getLibrarySettings();
            const viewType = (librarySettings?.viewType ?? "grid") as "grid" | "list";
            let field = librarySettings?.sortField ?? "document.modifiedAt";
            if (!field.startsWith("document.") && !field.startsWith("resource.")) {
                const legacySortFieldsMap = {
                    "modifiedAt": "document.modifiedAt",
                    "createdAt": "document.createdAt",
                    "name": "document.name",
                    "ownerName": "resource.owner.name",
                    "lastActivity": "resource.metadata.lastActivityByType.edited.createdAt"
                };
                field = legacySortFieldsMap[field] ?? `document.modifiedAt`;
            }
            const direction = typeof librarySettings?.sortReverse === "boolean" ? (librarySettings?.sortReverse ? "desc" : "asc") : "desc";

            PresentationLibraryPaneController.initialize({
                workspaceId,
                viewType,
                sort: [{ field, direction }],
                libraryFilter: { type: pane, folderId, subFolderId, presentationIds }
            });

            return () => {
                PresentationLibraryPaneController.reset();
            };
        }, [workspaceId, parentControllersInitialized]);

        useEffect(() => {
            if (!PresentationLibraryPaneController.initialized || !parentControllersInitialized) {
                return;
            }

            pane = pane ?? PresentationLibraryFilterType.ALL_PRESENTATIONS;

            PresentationLibraryPaneController.setLibraryFilter({ type: pane, folderId, subFolderId, presentationIds });
        }, [pane, presentationIds?.join(), folderId, subFolderId]);

        return children;
    }
)));
